import type { SummaryRow, SummaryStep } from "../schema/formModel.js";
import { NextSteps } from "./ContentBlocks.js";
import "./styles.css";

export const REVIEW_EMPTY_COPY = "There are no answers to check yet.";
export const REVIEW_CHANGE_LABEL = "Change";
/** A ticked consent reads as "Confirmed: <its sentence>" rather than "<sentence>: Yes". */
export const REVIEW_CONFIRMED_LABEL = "Confirmed";

export interface ReviewCard {
  title: string;
  lines: string[];
}

export interface ReviewStepProps {
  summary: ReadonlyArray<SummaryStep>;
  onChangeStep: (index: number) => void;
  /** KYC-design — group rows rendered as cards under their step, keyed by step index. */
  cards?: Readonly<Record<number, ReviewCard[]>>;
  /** Field names whose values render in monospace (identifiers). */
  monoFields?: ReadonlySet<string>;
  nextSteps?: ReadonlyArray<string>;
  declaration?: string;
}

/**
 * KYC-design — the review page: one bordered card per section with a Change link in its
 * header, terms in a 200px column, values formatted (dates, addresses, masked codes), people
 * as cards, then "What happens next" and the declaration sentence above Submit.
 */
export function ReviewStep({ summary, onChangeStep, cards, monoFields, nextSteps, declaration }: ReviewStepProps) {
  if (summary.length === 0) {
    return <p className="review-empty" role="status">{REVIEW_EMPTY_COPY}</p>;
  }
  return (
    <div className="review-summary">
      {summary.map((step) => {
        const stepCards = cards?.[step.index] ?? [];
        const rows = step.rows as ReadonlyArray<SummaryRow & { check?: boolean }>;
        return (
          <section className="review-step" key={step.index}>
            <div className="review-step-head">
              <h3 className="review-step-title">{step.title}</h3>
              <button
                type="button"
                className="review-change"
                aria-label={`${REVIEW_CHANGE_LABEL} ${step.title}`}
                onClick={() => onChangeStep(step.index)}
              >
                {REVIEW_CHANGE_LABEL}
              </button>
            </div>
            {rows.length > 0 && (
              <dl className="review-list">
                {rows.map((row) => row.check ? (
                  <div className="review-item review-item-check" key={row.name}>
                    <dt className="review-label review-label-check"><span className="review-check" aria-hidden="true" />{REVIEW_CONFIRMED_LABEL}</dt>
                    <dd className="review-value">{row.label}</dd>
                  </div>
                ) : (
                  <div className="review-item" key={row.name} style={{ display: "contents" }}>
                    <dt className="review-label">{row.label}</dt>
                    {/*
                      A row that reads back as described LINES shows those lines, one per entry
                      — the same text the subject saw under the input, so what they confirm and
                      what they typed read the same. It is prose as much as identifier, so it
                      does not take the monospace treatment a bare identifier does. Plain text
                      throughout: the renderer never injects markup.
                    */}
                    <dd className={`review-value${row.lines ? "" : (monoFields?.has(row.name) ? " review-value-mono" : "")}`}>
                      {row.lines
                        ? row.lines.map((line, i) => <span className="review-value-line" key={`${i}-${line}`}>{line}</span>)
                        : row.display}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
            {stepCards.length > 0 && (
              <div className="review-cards">
                {stepCards.map((card, i) => (
                  <div className="review-card" key={i}>
                    <p className="review-card-title">{card.title}</p>
                    {card.lines.map((line, j) => <p className="review-card-line" key={j}>{line}</p>)}
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })}
      {nextSteps && nextSteps.length > 0 && <NextSteps steps={nextSteps} />}
      {declaration && <p className="declaration">{declaration}</p>}
    </div>
  );
}
